import { useEffect, useState } from 'react'
import type { Block } from '@w/content-blocks'
import { ulid } from '@w/utils'
import { useViewportInset, withoutActWarning } from '@w/utils-fe'
import { BoldIcon, ItalicIcon, UnderlineIcon, StrikethroughIcon, CodeXmlIcon, Share2Icon, SquareIcon, SettingsIcon, MessageSquareIcon, ArrowUpIcon, ArrowDownIcon, Trash2Icon, IndentIncreaseIcon, IndentDecreaseIcon, GripVerticalIcon } from '@w/ui/components/icon'
import { Button } from '@w/ui/components/button'
import { Toolbar, ToolbarButton } from '@w/ui/components/toolbar'
import { blockClass, blockRange } from '../../core/block-selection'
import { Input } from '@w/ui/components/input'
import { activeFormats } from '../../core/selection'
import { formatToggles, formatToolbarVisible } from '../../core/format-actions'
import { selectedLink } from '../../core/rich-content'
import { children, locate, plain, pointAt, rows, sliceInline } from '../../core/tree-read'
import type { Overlay } from '../view-api'
import { OverlayPortal, focusedBlock, panelClass, select, usePosition, type OverlayApi } from './shared'
import { useReview } from '../review/context'
import { START_SELECTION_COMMENT_EVENT } from '../review/change-ranges'

const icons = { Bold: BoldIcon, Italic: ItalicIcon, Underline: UnderlineIcon, Strikethrough: StrikethroughIcon }

export const FloatingToolbar: Overlay = ({ api }: { api: OverlayApi }) => {
  const [inside, setInside] = useState(false)
  const [activated, setActivated] = useState(false)
  useEffect(() => {
    const root = api.root
    if (!root) return () => {}
    const refresh = () => {
      const active = root.ownerDocument.activeElement
      const next = root.contains(active) || !!active?.closest('[data-core-overlay]') && root.contains(root.ownerDocument.getSelection()?.anchorNode ?? null)
      withoutActWarning(() => { setInside(next); if (next) setActivated(true) })
    }
    refresh()
    root.ownerDocument.addEventListener('focusin', refresh)
    root.ownerDocument.addEventListener('focusout', refresh)
    return () => { root.ownerDocument.removeEventListener('focusin', refresh); root.ownerDocument.removeEventListener('focusout', refresh) }
  }, [api.root])
  return activated ? <FocusedFloatingToolbar api={api} inside={inside} /> : null
}

function FocusedFloatingToolbar({ api, inside }: { api: OverlayApi; inside: boolean }) {
  const { review } = useReview()
  const [panel, setPanel] = useState<HTMLDivElement | null>(null)
  const [settingsPanel, setSettingsPanel] = useState<HTMLFormElement | null>(null)
  const [settings, setSettings] = useState<{ block: Extract<Block, { type: 'cloze' }>; tip: string; answerRegexp: string } | null>(null)
  const { keyboardOpen } = useViewportInset()
  const [dock, setDock] = useState({ bottom: 0, left: 0, width: '100%' })
  useEffect(() => {
    const view = api.root?.ownerDocument.defaultView
    if (!view || api.layout !== 'touch') return () => {}
    const update = () => {
      const viewport = view.visualViewport
      setDock({ bottom: viewport ? Math.max(0, view.innerHeight - viewport.height - viewport.offsetTop) : 0,
        left: viewport?.offsetLeft ?? 0, width: viewport ? `${viewport.width}px` : '100%' })
    }
    update()
    view.addEventListener('resize', update)
    view.visualViewport?.addEventListener('resize', update)
    view.visualViewport?.addEventListener('scroll', update)
    return () => {
      view.removeEventListener('resize', update)
      view.visualViewport?.removeEventListener('resize', update)
      view.visualViewport?.removeEventListener('scroll', update)
    }
  }, [api.root, api.layout])
  const position = usePosition(api, undefined, true, settingsPanel ?? panel)
  const selection = api.state.selection
  const active = activeFormats(api.state)
  const testId = api.testIds?.toolbar ?? 'content-blocks-editor-toolbar'
  const selectionRows = rows(api.state.blocks)
  const anchor = selection && locate(selectionRows, selection.anchor)
  const focus = selection && locate(selectionRows, selection.focus)
  const block = focusedBlock(api)
  function containingCloze(items: readonly Block[], offset = 0): Extract<Block, { type: 'cloze' }> | undefined {
    if (!anchor || !focus || anchor.row !== focus.row) return undefined
    for (const block of items) {
      const end = offset + plain(block).length
      if (block.type === 'cloze' && Math.min(anchor.offset, focus.offset) >= offset && Math.max(anchor.offset, focus.offset) <= end) return block
      const found = containingCloze(children(block), offset)
      if (found) return found
      offset = end
    }
    return undefined
  }
  const cloze = anchor ? containingCloze(selectionRows[anchor.row]?.block.children ?? []) : undefined
  const link = selectedLink(api.state)
  // Cloze settings are DOM-only; they also show the toolbar on a caret inside a cloze.
  const textRange = !!selection && !selection.nodes?.length && !selection.table && (selection.anchor.blockId !== selection.focus.blockId || selection.anchor.offset !== selection.focus.offset)
  const caretActions = api.layout === 'touch' && !api.parentEditor && !!selection && !selection.nodes?.length && !selection.table && !textRange
  const visible = inside && api.mode === 'edit' && (api.layout !== 'touch' || keyboardOpen) && (caretActions || formatToolbarVisible(api.state) || !!cloze && !selection?.nodes?.length)
  const sameTextRow = !!selection && !selection.nodes?.length && !selection.table && !!anchor && !!focus && anchor.row === focus.row && anchor.offset !== focus.offset
  const selectedContent = sameTextRow && anchor && focus
    ? sliceInline(selectionRows[anchor.row]?.block.children ?? [], Math.min(anchor.offset, focus.offset), Math.max(anchor.offset, focus.offset)) : []
  const insideComment = selectedContent.length === 1 && selectedContent[0]?.type === 'comment'
  const canComment = !!review?.onAddComment && textRange && !insideComment
  function wrap(kind: 'code' | 'cloze') {
    if (!selection) return
    if (!cloze && selection.anchor.blockId === selection.focus.blockId && selection.anchor.offset === selection.focus.offset) return
    const entries = rows(api.state.blocks)
    const [from, to] = [locate(entries, selection.anchor), locate(entries, selection.focus)].toSorted((a, b) => a.row - b.row || a.offset - b.offset)
    if (kind === 'cloze' && cloze) {
      const unwrap = (items: readonly Block[]): Block[] => items.flatMap(block => block.id === cloze.id ? children(block)
        : children(block).length > 0 ? [Object.assign({}, block, { children: unwrap(children(block)) }) as Block] : [block])
      const row = entries[from.row]
      api.replaceBlock(row.block.id, Object.assign({}, row.block, { children: unwrap(row.block.children) }))
      return
    }
    const fragment: Block[] = entries.slice(from.row, to.row + 1).map((row, index) => {
      const content = sliceInline(row.block.children, index === 0 ? from.offset : 0, from.row + index === to.row ? to.offset : Infinity)
      function code(block: Block): Block {
        if (block.type === 'chars') return Object.assign({}, block, { format: active & 16 ? (block.format ?? 0) & ~16 : (block.format ?? 0) | 16 })
        if (block.type === 'text') return { id: '', type: 'chars', format: 16, text: [{ id: '', text: block.text }] }
        return children(block).length > 0 ? Object.assign({}, block, { children: children(block).map(child => code(child)) }) : block
      }
      return { id: '', type: 'p', children: kind === 'code' ? content.map(child => code(child)) : [{ id: '', type: 'cloze', qid: ulid(), fractionalIndex: '', children: content }] }
    })
    const result = api.dispatch({ type: 'insertFromPaste', md: '', fragment })
    const updated = rows(result.state.blocks)
    const first = updated.find(row => row.block.id === entries[from.row].block.id)
    const last = updated.find(row => row.block.id === entries[to.row].block.id)
    if (first && last) select(api, { anchor: pointAt(first, from.offset), focus: pointAt(last, to.offset) })
  }
  useEffect(() => {
    const root = api.root
    if (!root || api.mode !== 'edit') return () => {}
    const keydown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || !event.shiftKey || event.altKey || event.key.toLowerCase() !== 'c') return
      if (!selection || selection.nodes?.length || selection.table) return
      event.preventDefault(); event.stopImmediatePropagation(); wrap('cloze')
    }
    root.addEventListener('keydown', keydown, true)
    return () => { root.removeEventListener('keydown', keydown, true) }
  })
  if (!visible && !settings) return null
  const actions = <Toolbar ref={setPanel} variant={api.layout === 'touch' ? 'docked' : 'floating'} size={api.layout === 'touch' ? 'default' : 'sm'} aria-label="Text formatting" data-testid={testId}
    className={api.layout === 'touch' ? 'fixed z-50 min-h-12 flex-nowrap items-center gap-1 overflow-x-auto rounded-none border-t p-1 shadow-lg [&_button]:size-11 [&_button]:shrink-0' : 'z-40 items-center'}
    style={api.layout === 'touch' ? { bottom: dock.bottom, left: dock.left, width: dock.width } : position}
    onMouseDown={event => { if (!(event.target instanceof Element && event.target.closest('input,textarea'))) event.preventDefault() }}>
    {caretActions ? <>
      <ToolbarButton aria-label="Select block" tooltip="Select block" data-testid={`${testId}-select-block`}
        disabled={!block || blockClass(block) === 'header'} onClick={() => {
          if (block) select(api, blockRange(api.state.blocks, block.id, block.id))
        }}><GripVerticalIcon /></ToolbarButton>
      <ToolbarButton aria-label="Move up" tooltip="Move up" data-testid={`${testId}-move-up`} onClick={() => { api.dispatch({ type: 'moveBlock', direction: 'up' }) }}><ArrowUpIcon /></ToolbarButton>
      <ToolbarButton aria-label="Move down" tooltip="Move down" data-testid={`${testId}-move-down`} onClick={() => { api.dispatch({ type: 'moveBlock', direction: 'down' }) }}><ArrowDownIcon /></ToolbarButton>
      <ToolbarButton aria-label="Delete" tooltip="Delete" data-testid={`${testId}-delete`} onClick={() => {
        const row = focus && selectionRows[focus.row]
        if (!row) return
        select(api, blockRange(api.state.blocks, row.block.id, row.block.id))
        api.dispatch({ type: 'deleteContentBackward' })
      }}><Trash2Icon /></ToolbarButton>
      <ToolbarButton aria-label="Indent" tooltip="Indent" data-testid={`${testId}-indent`} onClick={() => { api.dispatch({ type: 'formatIndent' }) }}><IndentIncreaseIcon /></ToolbarButton>
      <ToolbarButton aria-label="Deindent" tooltip="Deindent" data-testid={`${testId}-deindent`} onClick={() => { api.dispatch({ type: 'formatOutdent' }) }}><IndentDecreaseIcon /></ToolbarButton>
    </> : <>
    {formatToggles.map(({ title, intent, flag }) => { const Icon = icons[title]; return <ToolbarButton key={title} type="button" tooltip={title} aria-label={title} aria-pressed={!!(active & flag)}
      data-testid={`${testId}-${title.toLowerCase()}`} className="aria-pressed:bg-primary/10 aria-pressed:text-primary"
      onClick={() => { api.dispatch(intent) }}><Icon /></ToolbarButton> })}
    {canComment && <ToolbarButton aria-label="Comment" tooltip="Comment" aria-keyshortcuts="c" data-testid={`${testId}-comment`}
      className={api.layout === 'touch' ? 'text-warning' : 'pointer-fine:hover:text-warning'} onClick={() => { api.root?.dispatchEvent(new Event(START_SELECTION_COMMENT_EVENT)) }}><MessageSquareIcon /></ToolbarButton>}
    <ToolbarButton type="button" tooltip="Code" aria-label="Code" aria-pressed={!!(active & 16)} data-testid={`${testId}-code`}
      className="aria-pressed:bg-primary/10 aria-pressed:text-primary" onClick={() => { wrap('code') }}><CodeXmlIcon /></ToolbarButton>
    <ToolbarButton type="button" tooltip="Link" aria-label="Link" aria-pressed={link !== null} data-testid={`${testId}-link`}
      className="aria-pressed:bg-primary/10 aria-pressed:text-primary" onClick={() => { api.root?.dispatchEvent(new CustomEvent('core-view-open-link')) }}><Share2Icon /></ToolbarButton>
    <ToolbarButton type="button" tooltip={cloze ? 'Remove cloze' : 'Cloze'} aria-label={cloze ? 'Remove cloze' : 'Cloze'} aria-pressed={!!cloze} data-testid={`${testId}-cloze`}
      className="aria-pressed:bg-primary/10 aria-pressed:text-primary" onClick={() => { wrap('cloze') }}><SquareIcon /></ToolbarButton>
    {cloze && <ToolbarButton type="button" tooltip="Cloze settings" aria-label="Cloze settings" data-testid={`${testId}-cloze-settings`}
      className="aria-pressed:bg-primary/10 aria-pressed:text-primary" onClick={() => { setSettings({ block: cloze, tip: cloze.tip ?? '', answerRegexp: cloze.answerRegexp ?? '' }) }}><SettingsIcon /></ToolbarButton>}
    {link !== null && <Input aria-label="Link URL" data-testid="floating-link-url" value={link}
      className="ml-1 h-7 w-48 rounded-md px-2 text-sm" onInput={event => { api.dispatch({ type: 'setLink', url: event.currentTarget.value }) }} />}
    </>}
  </Toolbar>
  return <OverlayPortal api={api}>{settings && <form ref={setSettingsPanel} role="dialog" aria-label="Cloze settings" className={`${panelClass} fixed z-[1100] grid w-72 gap-2`} style={position}
    onSubmit={event => { event.preventDefault(); api.replaceBlock(settings.block.id, { ...settings.block, tip: settings.tip || undefined, answerRegexp: settings.answerRegexp || undefined }); setSettings(null); api.root?.focus() }}>
    <label htmlFor={`cloze-hint-${settings.block.id}`}>Hint<Input id={`cloze-hint-${settings.block.id}`} aria-label="Cloze hint" value={settings.tip} onInput={event => { setSettings({ ...settings, tip: event.currentTarget.value }) }} /></label>
    <label htmlFor={`cloze-pattern-${settings.block.id}`}>Accepted answer pattern<Input id={`cloze-pattern-${settings.block.id}`} aria-label="Accepted answer pattern" value={settings.answerRegexp} onInput={event => { setSettings({ ...settings, answerRegexp: event.currentTarget.value }) }} /></label>
    <Button type="submit">Done</Button>
  </form>}{visible && actions}</OverlayPortal>
}
